import React from 'react';
import { useTranslation } from 'react-i18next';

import { ExitFullscreenIcon, FullscreenIcon, PageUpIcon, PageDownIcon } from '@seismic/icons';

import { Toolbar, ToolbarButton, ToolbarPageCounter, ToolbarSeparator } from './Toolbar';

import { toggleFullscreen } from '../utilities';
import hasFullscreenModiferKeys from '../utilities/hasFullscreenModiferKeys';

const PresentationToolbar = React.forwardRef(
  (
    {
      showToolbar,
      currentSlide,
      totalSlideCount,
      playerRef,
      psPdfKitInstance,
      isFullScreen,
      onPageBackClick,
      onPageNextClick,
      onGoToPage,
      mobile,
      handleKeyStroke,
      className,
      style
    },
    ref
  ) => {
    const { t } = useTranslation();

    // enables keyboard control without clicking into presentation
    if (psPdfKitInstance && psPdfKitInstance.contentWindow) {
      psPdfKitInstance.contentWindow.focus();
    }

    const forwardKeyEventToIframe = evt => {
      const iframe = document.querySelector('iframe');

      if (!iframe) {
        return;
      }

      if (iframe.contentDocument) {
        let forwardedEvent;

        try {
          forwardedEvent = new KeyboardEvent('keydown', { key: evt.key });
        } catch (e) {
          // Shim for IE, which does not support the Event constructor
          forwardedEvent = document.createEvent('KeyboardEvent');
          forwardedEvent.initKeyboardEvent(
            'keydown',
            true,
            true,
            window,
            evt.key,
            evt.location,
            evt.keyCode,
            '',
            evt.repeat
          );
        }

        iframe.contentDocument.dispatchEvent(forwardedEvent);
      }
    };

    const onKeyDown = event => {
      //Should not foward below navigation keys to Iframe
      let navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab', ' '];
      if (navigationKeys.indexOf(event.key) >= 0) return;
      handleKeyStroke ? handleKeyStroke(event) : forwardKeyEventToIframe(event);
      switch (event.key) {
        case 'f':
          if (hasFullscreenModiferKeys(event)) {
            toggleFullscreen(isFullScreen, playerRef);
          }
          break;
      }
    };

    return (
      <Toolbar
        datatest="presentationToolbar"
        type="float-width"
        show={showToolbar}
        ref={ref}
        mobile={mobile}
        // UP-499 - Enable keystrokes when the toolbar has focus
        // This makes this toolbar focusable
        // Otherwise, when clicked, the document's body gets focus
        // Which prevents the present mode key handlers from working properly
        tabIndex="0"
        // Now that the toolbar is focusable, call the handler if it's present,
        // otherwise fallback to forwarding the event to the iframe
        onKeyDown={onKeyDown}
        className={className}
        style={style}
      >
        <ToolbarButton
          icon={<PageUpIcon />}
          action={onPageBackClick}
          disabled={currentSlide <= 1}
          tooltip={t('Toolbar.Button.Tooltip.PreviousSlide', {
            defaultValue: 'Previous slide'
          })}
          datatest={'previousButton'}
        />
        <ToolbarButton
          icon={<PageDownIcon />}
          action={onPageNextClick}
          disabled={currentSlide === totalSlideCount}
          tooltip={t('Toolbar.Button.Tooltip.NextSlide', {
            defaultValue: 'Next slide'
          })}
          datatest={'nextButton'}
        />
        <ToolbarPageCounter
          goToPage={page => onGoToPage(page)}
          currentPage={currentSlide}
          totalPages={totalSlideCount}
          datatest={'slideCounter'}
        />
        <ToolbarSeparator datatest={'toolbarSeparator'} />
        <ToolbarButton
          icon={isFullScreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
          action={() => {
            toggleFullscreen(isFullScreen, playerRef, () => {
              ref.current && ref.current.focus();
            });
          }}
          tooltip={isFullScreen ? t('Toolbar.Button.Tooltip.FullScreenExit') : t('Toolbar.Button.Tooltip.FullScreen')}
          datatest={'fullScreenButton'}
        />
      </Toolbar>
    );
  }
);

export default PresentationToolbar;
